import React, { useState, useEffect } from 'react';
import { FiTrendingUp, FiTrendingDown, FiMinus, FiAlertCircle, FiCheckCircle, FiClock, FiAward } from 'react-icons/fi';
import { parentAnalyticsAPI } from '../../../api/parentEndpoints';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { showToast } from '../../../components/common/Toaster';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  Section,
  SectionTitle,
  Grid,
  StatCard,
  StatLabel,
  StatValue,
  StatDescription,
  Badge,
  ProgressBar,
  ProgressFill,
  CircularProgress,
  CircularProgressValue,
  AlertCard,
  LoadingContainer,
  EmptyState,
} from '../Analytics.styles';

const OverviewDashboard = ({ studentId, studentName }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchOverviewData();
  }, [studentId]);

  const fetchOverviewData = async () => {
    try {
      setLoading(true);
      
      // Fetch all necessary data for overview
      const [intelligence, performance, insights] = await Promise.all([
        parentAnalyticsAPI.getLearningIntelligence(studentId, { days: 30 }),
        parentAnalyticsAPI.getPerformanceAnalytics(studentId, { days: 30 }),
        parentAnalyticsAPI.getActionableInsights(studentId),
      ]);

      setData({
        intelligence: intelligence.data.learning_intelligence,
        performance: performance.data.performance_analytics,
        insights: insights.data.actionable_insights,
      });
    } catch (error) {
      showToast('error', 'Failed to load overview data');
      console.error('Error fetching overview:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEngagementColor = (score) => {
    if (score >= 75) return theme.colors.success;
    if (score >= 50) return theme.colors.warning;
    return theme.colors.error;
  };

  const getTrendIcon = (trend) => {
    if (!trend) return <FiMinus />;
    if (trend === 'improving') return <FiTrendingUp />;
    if (trend === 'declining') return <FiTrendingDown />;
    return <FiMinus />;
  };

  const getRatingStars = (rating) => {
    return '⭐'.repeat(Math.min(rating || 0, 5));
  };

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner size="large" text="Loading overview..." />
      </LoadingContainer>
    );
  }

  if (!data) {
    return (
      <EmptyState>
        <FiAlertCircle size={64} />
        <h3>No Data Available</h3>
        <p>Analytics data will appear once {studentName} starts learning</p>
      </EmptyState>
    );
  }

  const { intelligence, performance, insights } = data;

  return (
    <>
      {/* Critical Alerts */}
      {insights && insights.alerts && insights.alerts.length > 0 && (
        <Section>
          <SectionTitle>
            <FiAlertCircle />
            Attention Needed
          </SectionTitle>
          <Grid>
            {insights.alerts.slice(0, 3).map((alert, index) => (
              <AlertCard key={index} severity={alert.severity}>
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '24px', marginRight: '12px' }}>
                    {alert.severity === 'high' ? '🚨' : alert.severity === 'medium' ? '⚠️' : 'ℹ️'}
                  </span>
                  <div>
                    <h4>{alert.type.replace(/_/g, ' ').toUpperCase()}</h4>
                    <p>{alert.message}</p>
                  </div>
                </div>
              </AlertCard>
            ))}
          </Grid>
        </Section>
      )}

      {/* Key Metrics */}
      <Section>
        <SectionTitle>
          <FiCheckCircle />
          Key Performance Metrics
        </SectionTitle>
        <Grid minWidth="250px">
          {/* Engagement Score */}
          <StatCard>
            <StatLabel>Engagement Score</StatLabel>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <StatValue color={getEngagementColor(intelligence.engagement_score)}>
                {intelligence.engagement_score.toFixed(1)}%
              </StatValue>
              <CircularProgress
                percentage={intelligence.engagement_score}
                color={getEngagementColor(intelligence.engagement_score)}
                size="80px"
              >
                <CircularProgressValue style={{ fontSize: '18px' }}>
                  {intelligence.engagement_score.toFixed(0)}
                </CircularProgressValue>
              </CircularProgress>
            </div>
            <StatDescription>
              Rating: {getRatingStars(intelligence.engagement_rating)}
            </StatDescription>
          </StatCard>

          {/* Average Performance */}
          <StatCard>
            <StatLabel>Average Performance</StatLabel>
            <StatValue color={theme.colors.primary}>
              {performance.overall.average_score.toFixed(1)}%
            </StatValue>
            <div style={{ marginTop: '8px' }}>
              <ProgressBar>
                <ProgressFill 
                  percentage={performance.overall.average_score}
                  color={theme.colors.primary}
                />
              </ProgressBar>
            </div>
            <StatDescription style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '8px' }}>
              {getTrendIcon(performance.trajectory.trend)}
              <span style={{ textTransform: 'capitalize' }}>
                {performance.trajectory.trend} ({performance.trajectory.slope > 0 ? '+' : ''}{performance.trajectory.slope.toFixed(1)} pts/quiz)
              </span>
            </StatDescription>
          </StatCard>

          {/* Completion Rate */}
          <StatCard>
            <StatLabel>Quiz Pass Rate</StatLabel>
            <StatValue color={theme.colors.success}>
              {performance.overall.pass_rate.toFixed(1)}%
            </StatValue>
            <StatDescription>
              {performance.overall.passed_quizzes} / {performance.overall.total_quizzes} quizzes passed
            </StatDescription>
          </StatCard>

          {/* Study Streak */}
          <StatCard>
            <StatLabel>Study Streak</StatLabel>
            <StatValue style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              🔥 {intelligence.learning_stamina.study_streak}
            </StatValue>
            <StatDescription>
              {intelligence.learning_stamina.active_days} active days in last {intelligence.period_days} days
            </StatDescription>
          </StatCard>
        </Grid>
      </Section>

      {/* Learning Activity */}
      <Section>
        <SectionTitle>
          <FiClock />
          Learning Activity
        </SectionTitle>
        <Grid minWidth="300px">
          <StatCard>
            <StatLabel>Learning Velocity</StatLabel>
            <StatValue color={theme.colors.accent}>
              {intelligence.learning_velocity.toFixed(2)}
            </StatValue>
            <StatDescription>
              {intelligence.learning_velocity_description}
            </StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Study Time</StatLabel>
            <StatValue color={theme.colors.info}>
              {intelligence.learning_stamina.total_hours.toFixed(1)}h
            </StatValue>
            <StatDescription>
              Average: {intelligence.learning_stamina.avg_daily_minutes.toFixed(0)} min/day
            </StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Consistency Score</StatLabel>
            <StatValue color={theme.colors.secondary}>
              {intelligence.consistency_score.toFixed(0)}%
            </StatValue>
            <div style={{ marginTop: '8px' }}>
              <ProgressBar>
                <ProgressFill 
                  percentage={intelligence.consistency_score}
                  color={theme.colors.secondary}
                />
              </ProgressBar>
            </div>
          </StatCard>
        </Grid>
      </Section>

      {/* Comprehension & Mastery */}
      <Section>
        <SectionTitle>
          <FiAward />
          Comprehension & Mastery
        </SectionTitle>
        <Grid minWidth="250px">
          <StatCard>
            <StatLabel>Comprehension Rate</StatLabel>
            <StatValue color={theme.colors.success}>
              {intelligence.comprehension_rate.toFixed(1)}%
            </StatValue>
            <StatDescription>
              Rating: {getRatingStars(intelligence.comprehension_rating)}
            </StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Peak Performance Hour</StatLabel>
            <StatValue style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              🕐 {intelligence.peak_performance_hour}:00
            </StatValue>
            <StatDescription>
              Score: {intelligence.peak_performance_score.toFixed(1)}%
            </StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Recent Average</StatLabel>
            <StatValue color={theme.colors.primary}>
              {performance.overall.recent_average.toFixed(1)}%
            </StatValue>
            <StatDescription>
              Last 5 quizzes performance
            </StatDescription>
          </StatCard>
        </Grid>
      </Section>

      {/* Subject Mastery Preview */}
      {performance.subject_mastery && performance.subject_mastery.length > 0 && (
        <Section>
          <SectionTitle>Top Performing Subjects</SectionTitle>
          <Grid minWidth="300px">
            {performance.subject_mastery.slice(0, 3).map((subject) => (
              <StatCard key={subject.subject_id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <StatLabel>{subject.subject_name}</StatLabel>
                  <Badge color={theme.colors.success}>
                    {subject.mastery_level}
                  </Badge>
                </div>
                <StatValue color={theme.colors.primary}>
                  {subject.average_score.toFixed(1)}%
                </StatValue>
                <StatDescription>
                  Pass Rate: {subject.pass_rate.toFixed(0)}% • {subject.attempts} attempts
                </StatDescription>
                <div style={{ marginTop: '12px' }}>
                  <ProgressBar>
                    <ProgressFill 
                      percentage={subject.average_score}
                      color={theme.colors.success}
                    />
                  </ProgressBar>
                </div>
              </StatCard>
            ))}
          </Grid>
        </Section>
      )}

      {/* Positive Insights */}
      {insights && insights.positive_insights && insights.positive_insights.length > 0 && (
        <Section>
          <SectionTitle>
            ✨ Recent Achievements
          </SectionTitle>
          <Grid>
            {insights.positive_insights.slice(0, 3).map((insight, index) => (
              <AlertCard key={index} severity="positive">
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '24px', marginRight: '12px' }}>🎉</span>
                  <div>
                    <h4>{insight.type.replace(/_/g, ' ').toUpperCase()}</h4>
                    <p>{insight.message}</p>
                  </div>
                </div>
              </AlertCard>
            ))}
          </Grid>
        </Section>
      )}
    </>
  );
};

export default OverviewDashboard;

